import PropTypes from 'prop-types'
import React, { Component } from 'react'
import classNames from 'classnames'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { TooltipOnHover } from 'components/Utils/TooltipOnHover'
import * as Icons from 'components/Utils/Icons'
import CommentsRecord from 'data/comments/record'
import { selectors as UsersSelectors, record as UsersRecord } from 'data/users'
import { providesCommentEditionControls } from '../dataSync'

import { UserAvatar } from 'components/Utils/Avatar'
import CommentContent from './Content'
import Options from './Options'
import Highlight from './Highlight'
import SendAndCancel from './Commands/SendAndCancel'
import Replies from './Replies'
import { Text } from 'components/base'

import { makeHoverable } from 'components/Utils/hoverable'

import { getCommentTime, getCommentDate } from '../utils'

class Comment extends Component {
  state = {
    updatingComment: false,
  }

  static propTypes = {
    comment: PropTypes.instanceOf(CommentsRecord).isRequired,
    author: PropTypes.instanceOf(UsersRecord), // not fetched if user deleted?
    showActivity: PropTypes.bool,
    highlight: PropTypes.string,
    highlighted: PropTypes.bool.isRequired,
    isResolved: PropTypes.bool.isRequired,
    // hoverable
    isHovered: PropTypes.bool.isRequired,
    onMouseOver: PropTypes.func.isRequired,
    onMouseLeave: PropTypes.func.isRequired,
  }

  constructor(props) {
    super(props)
    this.commentContent = React.createRef()
  }

  onStopEditing = () => {
    this.setState({ updatingComment: false })
  }

  onCommentEdit = () => {
    this.setState({ updatingComment: true })
  }

  onSubmit = () => {
    this.commentContent.current.submit()
  }

  onCancel = () => {
    this.onStopEditing()
  }

  onClickInner = event => {
    if (
      !event.defaultPrevented &&
      !this.state.updatingComment &&
      this.props.onReply
    ) {
      this.props.onReply()
    }
  }

  renderActivity = () => {
    if (this.props.notificationCount > 0) {
      return (
        <div
          className="shade-100-important transition position-absolute background-red bold center-text flexbox flexbox-centered radius-round  font-size-small"
          style={{
            width: 20,
            height: 20,
            right: 24,
            top: 16,
          }}
        >
          {this.props.notificationCount}
        </div>
      )
    }
    if (this.props.showActivity) {
      return (
        <div
          className="shade-100-important transition position-absolute background-red bold center-text flexbox flexbox-centered radius-round "
          style={{
            width: 8,
            height: 8,
            right: 24,
            top: 16,
          }}
        />
      )
    }
    return null
  }

  renderOptions = () => {
    if (this.state.updatingComment) {
      return null
    }

    const {
      comment,
      isHovered,
      onReply,
      author,
      noteId,
      currentUser,
    } = this.props

    return (
      <div
        className={classNames(
          'position-absolute zindex-small',
          'flexbox flexbox-align-center'
        )}
        style={{ right: 24, top: 0, marginTop: -8 }}
      >
        <Options
          isHovered={isHovered}
          commentId={comment.id}
          threadId={comment.threadId}
          noteId={noteId}
          onCommentEdit={this.onCommentEdit}
          commentContent={comment.content}
          onReply={onReply}
          isCurrentUserCommentAuthor={author && currentUser.id === author.id}
        />
      </div>
    )
  }
  renderCommentHeader = () => {
    const { comment, author, sameAuthorBefore, isMainPanel } = this.props
    return (
      // Join comments if in thread panel & if same author
      !isMainPanel && sameAuthorBefore ? (
        ''
      ) : (
        <div className="flexbox flexbox-align-center margin-bottom-small">
          <div
            className="margin-right-small font-weight-bold"
            style={{ fontSize: 15 }}
          >
            @{author && author.username}
          </div>
          <TooltipOnHover
            id="comment=creation-date"
            placement="top"
            tooltipContent={`${getCommentDate(
              comment.createdAt
            )} at ${getCommentTime(comment.createdAt)}`}
          >
            <Text.SmallDetail>
              {getCommentTime(comment.createdAt)}
            </Text.SmallDetail>
          </TooltipOnHover>
        </div>
      )
    )
  }

  render() {
    const {
      comment,
      sameAuthorBefore,
      sameAuthorAfter,
      onMouseOver,
      onMouseLeave,
      isHovered,
      highlight,
      highlighted,
      isResolved,
      isMainPanel,
      onReply,
      numberOfReplies,
      repliers,
    } = this.props

    const hasReplies = numberOfReplies > 0

    return (
      <div
        className="position-relative flexbox width-full"
        onMouseOver={onMouseOver}
        onMouseLeave={onMouseLeave}
      >
        {this.renderOptions()}
        <div
          style={{
            transition: 'background .5s ease-in-out',
          }}
          className={classNames(
            'flexbox width-full',
            'padding-horizontal-large',
            !isMainPanel && sameAuthorAfter
              ? 'padding-bottom-smaller'
              : 'padding-bottom-medium',
            !isMainPanel && sameAuthorBefore
              ? 'padding-top-smaller'
              : 'padding-top-large',
            {
              'background-primary-light': highlighted,
              'background-shade-50': !highlighted && !isResolved && isHovered,
              'background-stripped-shade-100': !highlighted && isResolved,
              pointer: !this.state.updatingComment && onReply,
            }
          )}
          onClick={this.onClickInner}
        >
          <div
            className="padding-right-normal"
            style={{
              opacity: !isMainPanel && sameAuthorBefore ? 0 : 1,
              height: !isMainPanel && sameAuthorBefore ? 0 : 'auto',
            }}
          >
            <UserAvatar size="base" userId={comment.createdBy} noBorder />
          </div>
          <div className="width-full">
            {this.renderActivity()}
            {this.renderCommentHeader()}

            {highlight && (
              <Highlight content={highlight} threadId={comment.threadId} />
            )}
            <div>
              <CommentContent
                ref={this.commentContent}
                comment={comment}
                updatingComment={this.state.updatingComment}
                updateComment={this.props.updateComment}
                onStopEditing={this.onStopEditing}
              />
            </div>
            {this.state.updatingComment && (
              <div className="width-full flexbox flexbox-align-center  margin-top-normal">
                <div className="margin-left-auto">
                  <SendAndCancel
                    onSubmit={this.onSubmit}
                    onCancel={this.onCancel}
                    submitLabel="Save"
                  />
                </div>
              </div>
            )}
            {hasReplies && (
              <Replies
                numberOfReplies={numberOfReplies}
                repliers={repliers}
                onReply={onReply}
              />
            )}
            {isMainPanel &&
              !hasReplies && (
                <div
                  className="flexbox flexbox-align-center pointer margin-top-normal font-weight-medium shade-20 shade-10-on-hover"
                  onClick={this.props.goToMainPanelView}
                >
                  <Icons.NewCommentIcon size={12} />
                  <Text.Action className="margin-left-normal">
                    Reply
                  </Text.Action>
                </div>
              )}
          </div>
        </div>
      </div>
    )
  }
}

const mapStateToProps = (state, { comment }) => ({
  currentUser: UsersSelectors.getCurrentUser(state),
  author: UsersSelectors.getUser(state, comment.createdBy),
})

export default compose(
  connect(mapStateToProps),
  makeHoverable(),
  providesCommentEditionControls
)(Comment)
